import { AppIcon, ICON_SIZES, PriorityPlaylist } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Avatar, Box, Button, Chip, SxProps, Theme, Typography } from '@mui/material';

import {
  SUGGESTED_PLAYLIST_CARD_IMAGE_HEIGHT,
  SUGGESTED_PLAYLIST_CARD_WIDTH,
} from '../constants/priorities.constants';

const PLAYLIST_CARD_STRINGS = appStrings.modules.priorities.playlistCard;

type SuggestedPlaylistCardProps = Readonly<{
  playlist: PriorityPlaylist;
  handlePlaylistClick: (playlist: PriorityPlaylist) => void;
  cardWidth?: number;
  cardHeight?: number;
  fitWidth?: boolean;
  shouldSetCardWidth?: boolean;
  cardSize?: 'SMALL' | 'LARGE';
  isInCoachingSession?: boolean;
  isHighlighted?: boolean;
}>;

export const SuggestedPlaylistCard = ({
  playlist,
  handlePlaylistClick,
  cardWidth = undefined,
  cardHeight = undefined,
  fitWidth = false,
  shouldSetCardWidth = true,
  cardSize = 'LARGE',
  isInCoachingSession = false,
  isHighlighted = false,
}: SuggestedPlaylistCardProps) => {
  return (
    <Box
      sx={playlistCardSx(
        cardWidth,
        cardHeight,
        shouldSetCardWidth,
        fitWidth,
        isInCoachingSession,
        isHighlighted,
      )}
      onClick={() => handlePlaylistClick(playlist)}
    >
      <Box sx={playlistImageSx} component="img" src={playlist.preview_media?.media_url} />
      <Chip
        variant="pill"
        size="sm"
        label={PLAYLIST_CARD_STRINGS.tasksCount(playlist.total_tasks)}
        sx={taskCountChipSx}
      />
      <Chip
        variant="badge"
        size="sm"
        label={'Backrs'}
        sx={backrsChipSx}
        icon={<Avatar src={'/images/backrs-small.png'} sx={avatarSx} />}
      />
      <Box sx={playlistContentSx}>
        <Typography variant="text-lg-semibold" sx={titleSx(cardSize)}>
          {playlist.title}
        </Typography>
        <Typography variant="text-sm-regular" sx={descriptionSx(cardSize)}>
          {playlist.marketing_description}
        </Typography>

        <Button
          id="suggested-playlist-add-button"
          hierarchy="Secondary gray"
          size="lg"
          fullWidth
          sx={addPlaylistButtonSx(isInCoachingSession)}
          onClick={() => handlePlaylistClick(playlist)}
        >
          <AppIcon iconName="Plus" diameter="20px" color="currentColor" />

          <Typography variant="text-sm-semibold">{PLAYLIST_CARD_STRINGS.addPlaylist}</Typography>
        </Button>
      </Box>
    </Box>
  );
};

const playlistCardSx =
  (
    cardWidth: number | undefined,
    cardHeight: number | undefined,
    shouldSetCardWidth: boolean,
    fitWidth: boolean,
    disableHoverEffects: boolean,
    isHighlighted: boolean,
  ): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    borderRadius: backrs.radius['radius-4xl'],
    border: `1px solid ${isHighlighted ? backrs.colors['Brand/300'] : backrs.colors['Colors/Base/Black']}`,
    overflow: 'hidden',
    width: shouldSetCardWidth ? cardWidth || SUGGESTED_PLAYLIST_CARD_WIDTH : '100%',
    minWidth: shouldSetCardWidth ? cardWidth || SUGGESTED_PLAYLIST_CARD_WIDTH : undefined,
    maxWidth: !fitWidth ? cardWidth || SUGGESTED_PLAYLIST_CARD_WIDTH : '100%',
    ...(cardHeight && {
      height: cardHeight,
    }),
    position: 'relative',
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],

    cursor: 'pointer',
    userSelect: 'none',

    transition: 'all 0.3s ease-in-out',

    ...(isHighlighted && {
      outline: `2px solid ${backrs.colors['Brand/500']}`,
      outlineOffset: '2px',
    }),

    ...(!disableHoverEffects && {
      '&:hover': {
        borderColor: backrs.colors['Colors/Foreground/fg-primary'],
        transform: 'translate(-2px, -2px)',
        boxShadow: backrs.shadows['shadow-lg'],
      },
    }),

    '&:active': {
      transform: 'none !important',
      boxShadow: 'none !important',
      transition: 'all 0s !important',
    },
  });

const playlistImageSx: SxProps<Theme> = ({ backrs }) => ({
  objectFit: 'cover',
  width: '100%',
  height: '100%',
  maxHeight: SUGGESTED_PLAYLIST_CARD_IMAGE_HEIGHT,
  minHeight: SUGGESTED_PLAYLIST_CARD_IMAGE_HEIGHT,
  borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const taskCountChipSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  top: backrs.spacing['spacing-xl'],
  right: backrs.spacing['spacing-xl'],
  position: 'absolute',
  backgroundColor: backrs.colors['Colors/Background/bg-active'],
});

const backrsChipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  paddingLeft: backrs.spacing['spacing-none'],
  gap: backrs.spacing['spacing-lg'],
  position: 'absolute',
  top: SUGGESTED_PLAYLIST_CARD_IMAGE_HEIGHT - 40,
  left: backrs.spacing['spacing-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const avatarSx: SxProps<Theme> = {
  width: ICON_SIZES['16px'],
  height: ICON_SIZES['16px'],
};

const titleSx = (cardSize: 'SMALL' | 'LARGE'): SxProps<Theme> => ({
  display: '-webkit-box',
  WebkitLineClamp: cardSize === 'SMALL' ? 1 : 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
});

const descriptionSx =
  (cardSize: 'SMALL' | 'LARGE'): SxProps<Theme> =>
  ({ backrs }) => ({
    display: '-webkit-box',
    WebkitLineClamp: cardSize === 'SMALL' ? 2 : 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    mb: backrs.spacing['spacing-xl'],
  });

const playlistContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-xxs'],
  paddingX: backrs.spacing['spacing-xl'],
  paddingY: backrs.spacing['spacing-2xl'],
  width: '100%',
  height: '100%',
  flex: 1,
});

const addPlaylistButtonSx =
  (addHoverHighlight: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    mt: 'auto',
    gap: backrs.spacing['spacing-xs'],
    color: backrs.colors['Colors/Text/text-primary'],
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    '&:hover': {
      backgroundColor: addHoverHighlight ? backrs.colors['Brand/500'] : backrs.colors['Gray/50'],
      color: addHoverHighlight ? backrs.colors['Colors/Base/White'] : 'inherit',
      border: `1px solid ${backrs.colors[addHoverHighlight ? 'Colors/Base/Black' : 'Colors/Border/border-primary']}`,
    },
  });
